import { useMemo, useState } from 'react';
import { Alert, Button, Chip, Stack, TextField, IconButton, MenuItem, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import UploadIcon from '@mui/icons-material/UploadFile';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/DeleteOutline';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import {
  PageHeader,
  DataTable,
  ConfirmDialog,
  AdminPageState,
  ListFiltersBar,
} from '@shared/components';
import { useApiQuery } from '@shared/hooks/useApi.js';
import { api } from '@core/api.js';
import { useNotify } from '@shared/hooks/useNotify.js';
import { useFeature } from '@core/offerContext.jsx';
import { useDebouncedValue } from '@shared/hooks/useDebouncedValue.js';
import { apiErrorMessage } from '@shared/utils/apiErrorMessage.js';
import normalizeListResponse from '@shared/utils/normalizeListResponse.js';
import {
  colText,
  colSecondary,
  colDate,
  colStatus,
  colActions,
} from '@shared/utils/dataGridColumns.jsx';
import ContactFormDialog from '../components/ContactFormDialog.jsx';

const STATUS_FILTER = [
  { value: '', label: 'Tous les statuts' },
  { value: 'lead', label: 'Lead' },
  { value: 'qualified', label: 'Qualifié' },
  { value: 'customer', label: 'Client' },
  { value: 'lost', label: 'Perdu' },
];

const SOURCE_FILTER = [
  { value: '', label: 'Toutes les sources' },
  { value: 'website-home-meeting', label: 'Rencontre à domicile' },
  { value: 'website-quote-request', label: 'Demande de devis' },
  { value: 'website-contact', label: 'Contact site' },
  { value: 'website-booking', label: 'Réservation' },
  { value: 'public-api', label: 'API publique' },
];

const SOURCE_LABELS = {
  'website-home-meeting': 'Rencontre à domicile',
  'website-quote-request': 'Demande de devis',
  'quote-request': 'Demande de devis',
  'website-contact': 'Contact site',
  'public-api': 'API publique',
  'website-booking': 'Réservation',
  booking: 'Réservation',
};

const STATUS_MAP = {
  lead: { label: 'Lead', color: 'info' },
  qualified: { label: 'Qualifié', color: 'warning' },
  customer: { label: 'Client', color: 'success' },
  lost: { label: 'Perdu', color: 'default' },
};

function formatSourceLabel(source) {
  if (!source) return '—';
  return SOURCE_LABELS[source] || source;
}

export default function ContactsPage() {
  const navigate = useNavigate();
  const notify = useNotify();
  const csvEnabled = useFeature('contact.csvImport');
  const [status, setStatus] = useState('');
  const [source, setSource] = useState('');
  const [q, setQ] = useState('');
  const debouncedQ = useDebouncedValue(q, 300);
  const [editing, setEditing] = useState(null);
  const [formPreset, setFormPreset] = useState(null);
  const [confirming, setConfirming] = useState(null);
  const [pendingRowId, setPendingRowId] = useState(null);

  const query = new URLSearchParams();
  if (status) query.append('status', status);
  if (source) query.append('source', source);
  if (debouncedQ) query.append('q', debouncedQ);

  const {
    data,
    isLoading,
    isError,
    error,
    refetch,
  } = useApiQuery(
    ['contacts', status, source, debouncedQ],
    `/v1/contacts${query.toString() ? `?${query}` : ''}`,
  );

  const { items: rows, meta } = useMemo(() => normalizeListResponse(data), [data]);

  const remove = async () => {
    if (!confirming?.id) return;
    setPendingRowId(confirming.id);
    try {
      await api.delete(`/v1/contacts/${confirming.id}`);
      notify.success('Contact supprimé');
      refetch();
    } catch (err) {
      notify.error(apiErrorMessage(err, 'Suppression du contact impossible.'));
    }
    setPendingRowId(null);
    setConfirming(null);
  };

  const importCsv = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append('file', file);
    try {
      const { data: res } = await api.post('/v1/contacts/import', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      notify.success(`${res.imported} contact(s) importé(s)`);
      refetch();
    } catch (err) {
      notify.error(apiErrorMessage(err, 'Import CSV impossible.'));
    }
  };

  const columns = useMemo(() => [
    colText('name', 'Nom', { flex: 1.1, minWidth: 160, fontWeight: 600 }),
    colSecondary('email', 'Email', { flex: 1, minWidth: 180 }),
    colSecondary('phone', 'Téléphone', { width: 130 }),
    {
      field: 'source',
      headerName: 'Source',
      width: 180,
      sortable: true,
      valueGetter: (value, row) => formatSourceLabel((row || value?.row)?.source),
      renderCell: ({ row }) => {
        const raw = row?.source || '';
        const label = formatSourceLabel(raw);
        if (!raw) {
          return <Typography variant="body2" color="text.secondary">—</Typography>;
        }
        const highlight = raw === 'website-home-meeting';
        return (
          <Chip
            size="small"
            label={label}
            color={highlight ? 'warning' : 'default'}
            variant={highlight ? 'filled' : 'outlined'}
          />
        );
      },
    },
    colStatus('status', 'Statut', STATUS_MAP, { width: 120 }),
    colDate('createdAt', 'Créé', { width: 110 }),
    colActions(
      ({ row }) => (
        <Stack direction="row" justifyContent="flex-end">
          <IconButton
            size="small"
            aria-label="modifier le contact"
            disabled={pendingRowId === row.id}
            onClick={(e) => {
              e.stopPropagation();
              setEditing(row);
            }}
          >
            <EditIcon fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            aria-label="Supprimer le contact"
            color="error"
            disabled={pendingRowId === row.id}
            onClick={(e) => {
              e.stopPropagation();
              setConfirming(row);
            }}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Stack>
      ),
      { width: 100 },
    ),
  ], [pendingRowId]);

  const hasFilters = !!(status || source || debouncedQ);

  return (
    <>
      <PageHeader
        dense
        title="Contacts"
        subtitle="Pilotez vos leads et transformez-les en clients."
        actions={(
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {csvEnabled && (
              <Button component="label" variant="outlined" size="small" startIcon={<UploadIcon />}>
                Importer CSV
                <input hidden accept=".csv" type="file" onChange={importCsv} />
              </Button>
            )}
            <Button
              variant="outlined"
              size="small"
              startIcon={<AddIcon />}
              onClick={() => { setFormPreset(null); setEditing({}); }}
            >
              Nouveau contact
            </Button>
            <Button
              variant="contained"
              size="small"
              startIcon={<AddIcon />}
              onClick={() => { setFormPreset('client'); setEditing({}); }}
            >
              Nouveau client
            </Button>
          </Stack>
        )}
      />
      <ListFiltersBar>
        <TextField
          size="small"
          select
          label="Statut"
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          sx={{ minWidth: 160 }}
        >
          {STATUS_FILTER.map((s) => (
            <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>
          ))}
        </TextField>
        <TextField
          size="small"
          select
          label="Source"
          value={source}
          onChange={(e) => setSource(e.target.value)}
          sx={{ minWidth: 200 }}
        >
          {SOURCE_FILTER.map((s) => (
            <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>
          ))}
        </TextField>
        <TextField
          size="small"
          label="Rechercher"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          sx={{ flex: 1, minWidth: 200 }}
        />
      </ListFiltersBar>
      {!isLoading && !isError && rows.length === 0 && !hasFilters && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Liste vide pour ce workspace
          {meta?.workspaceId ? ` (${meta.workspaceId})` : ''}.
          En base, les UUID apparaissent parfois en binaire (0x…) : c’est le même identifiant.
          Si vous voyez déjà des lignes dans <code>crm_deal</code>, ouvrez{' '}
          <Button component={RouterLink} to="/crm" size="small" sx={{ verticalAlign: 'baseline', minWidth: 0, p: 0 }}>
            Pipeline CRM
          </Button>
          {' '}ou rechargez après déploiement. Sinon vérifiez que la{' '}
          <Button component={RouterLink} to="/api-keys" size="small" sx={{ verticalAlign: 'baseline', minWidth: 0, p: 0 }}>
            clé API
          </Button>
          {' '}publique est bien liée à ce workspace.
        </Alert>
      )}
      <AdminPageState
        isLoading={isLoading}
        isError={isError}
        error={error}
        onRetry={refetch}
        isEmpty={rows.length === 0}
        emptyVariant={hasFilters ? 'filtered' : 'initial'}
        emptyTitle={hasFilters ? 'Aucun contact ne correspond' : 'Aucun contact pour l’instant'}
        emptyDescription={
          hasFilters
            ? 'Modifiez la recherche ou les filtres pour afficher toute la base.'
            : 'Les demandes de première visite du site apparaissent ici (source Rencontre à domicile). Vérifiez aussi que la clé API publique est dans le même workspace.'
        }
      >
        <DataTable
          rows={rows}
          columns={columns}
          loading={isLoading}
          density="compact"
          onRowClick={({ row }) => navigate(`/contacts/${row.id}`)}
        />
      </AdminPageState>
      {editing !== null && (
        <ContactFormDialog
          contact={editing}
          preset={formPreset}
          onClose={(didMutate = false) => {
            setEditing(null);
            setFormPreset(null);
            if (didMutate) refetch();
          }}
        />
      )}
      <ConfirmDialog
        open={!!confirming}
        title="Supprimer le contact"
        message={`${confirming?.name ?? 'Ce contact'} sera retiré de la base. Cette action est définitive.`}
        destructive
        confirmLabel="Supprimer définitivement"
        loadingLabel="Suppression…"
        loading={!!pendingRowId}
        onCancel={() => setConfirming(null)}
        onConfirm={remove}
      />
    </>
  );
}
